/**
 * The URL is the state.
 *
 * `ARCHITECTURE.md` §Frontend: "There is no global client store… The URL is the
 * state: period, filters, tabs, selected rows." That is a good rule and a tedious
 * one to follow by hand — read `useSearch`, parse it, merge a patch, navigate — so
 * it is one hook, and sessions 19 and 20 write `?period=2026-08` with three lines.
 *
 * ```ts
 * const PeriodSearch = z.object({ period: PeriodSchema.default(currentPeriod) })
 * const [search, setSearch] = useSearchState(PeriodSearch)
 * <button onClick={() => { setSearch({ period: previous }) }}>…</button>
 * ```
 *
 * The schema is the same kind of object the API validates with
 * (`packages/shared/src/schemas/`), so a period in a URL and a period in a body
 * are the same rule. A value the schema refuses falls back to the schema's own
 * defaults rather than throwing: a hand-edited or stale URL should show the page,
 * not an error boundary.
 *
 * **This file is that rule, both halves of it**, and a route needs the other half:
 *
 *   - `useSearchState(schema)` — what a *page* reads and writes.
 *   - `routeSearch(schema)` — what a *route* hands to `validateSearch`.
 *
 * The hook is **not** a replacement for `validateSearch`. A route that owns its
 * search declares it there and gets full type inference from the router, and a
 * search parameter no route declares is one the router is free to drop across a
 * navigation — which is the back button quietly not working. So the two are used
 * together, and `routeSearch` is what stops the declaration being the strict half:
 * a **bare** zod schema in `validateSearch` throws `SearchParamError` on any value
 * it refuses, and the router's boundary then replaces the whole page — shell,
 * sidebar and all. Session 24 found ten routes doing exactly that; `?section=`
 * guessed wrong on one client took down every pixel of the studio.
 */

import { useNavigate, useSearch, type SearchSchemaInput } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import type { z } from 'zod'

export type SetSearchState<T> = (patch: Partial<T>, options?: { replace?: boolean }) => void

export function useSearchState<T extends Record<string, unknown>>(
  schema: z.ZodType<T>,
): readonly [T, SetSearchState<T>] {
  const raw = useSearch({ strict: false })
  const navigate = useNavigate()

  const value = useMemo(() => parseOrDefault(schema, raw), [schema, raw])

  const set = useCallback<SetSearchState<T>>(
    (patch, options) => {
      void navigate({
        to: '.',
        search: (previous: Record<string, unknown>) => ({ ...previous, ...patch }),
        replace: options?.replace ?? false,
      })
    },
    [navigate],
  )

  return [value, set] as const
}

/**
 * A route's `validateSearch`, which cannot throw over a value somebody typed.
 *
 * ```ts
 * export const Route = createFileRoute('/studio/tasks/')({
 *   validateSearch: routeSearch(TasksSearchSchema),
 *   component: TasksPage,
 * })
 * ```
 *
 * The declaration is still the schema's, so `<Link to="/studio/tasks" search={{
 * filter: 'overdue' }}>` is typed and the router keeps `filter` across a
 * navigation; what this adds is `parseOrDefault` between the router and the
 * schema. The router calls a **function** validator directly (`router-core`
 * `validateSearch`: `~standard` first, then `parse`, then the function), which is
 * why this returns a function and not a schema — a schema would be handed back to
 * the standard-schema path and throw again.
 *
 * `schema` is carried on the returned function on purpose: `routes.test.tsx`
 * walks the whole route tree and asks every route what keys it declares, so a
 * route added with a bare schema — or with a hand-rolled validator that answers
 * nothing — fails there rather than in somebody's address bar.
 *
 * **The parameter is the schema's `input`, branded `SearchSchemaInput`**, and that
 * brand is load-bearing rather than decorative. `router-core`'s
 * `ResolveSearchValidatorInputFn` reads a *function* validator's argument type
 * only when it carries the brand; without it the router falls back to the
 * function's **return** type as the input, so `section: 'overview' | …` — required
 * on the way out because it has a default — becomes required on the way in, and
 * `<Link to="/studio/clients/$slug">` stops compiling in five places. A schema in
 * `validateSearch` gets its input from `~standard` and never has this problem,
 * which is why the brand only shows up here. Nothing constructs one at runtime:
 * the router hands this the parsed query string, exactly as it does a schema.
 */
export type RouteSearch<TOut extends Record<string, unknown>, TIn> = ((
  raw: TIn & SearchSchemaInput,
) => TOut) & {
  /** What the fallback is taken from, and what the route tree test enumerates. */
  readonly schema: z.ZodType<TOut, TIn>
}

export function routeSearch<TOut extends Record<string, unknown>, TIn>(
  schema: z.ZodType<TOut, TIn>,
): RouteSearch<TOut, TIn> {
  return Object.assign((raw: TIn & SearchSchemaInput): TOut => parseOrDefault(schema, raw), {
    schema,
  })
}

/**
 * The schema's answer, minus the keys it refused, or the schema's answer to nothing.
 *
 * Three parses rather than one, and the middle one is the point:
 *
 *   1. the whole address, which is the answer almost every time;
 *   2. the address **without the keys the schema named in its issues** — so
 *      `?section=nope&period=2026-07` is July's overview rather than August's,
 *      because one bad tab is no reason to throw away a good month;
 *   3. `{}`, which a schema whose fields all have `.default()` answers with the
 *      page's initial state. A schema with a genuinely required field has no
 *      default to fall back to and throws here, which is correct — that is a route
 *      that cannot be rendered without the value, and it belongs in a `beforeLoad`
 *      redirect rather than in a fallback.
 *
 * A key the schema does not know is **not** a refusal: `z.object` is not strict,
 * so `?utm_source=mail` parses at step 1 and is dropped from the parsed value.
 * That is deliberate — a tracking parameter, or one this page used to have, must
 * not change what the page shows — and it is also the one thing this cannot see,
 * which is why `warnRefused` exists.
 */
export function parseOrDefault<T extends Record<string, unknown>>(
  schema: z.ZodType<T>,
  raw: unknown,
): T {
  const parsed = schema.safeParse(raw)
  if (parsed.success) return parsed.data

  const refused = refusedKeys(parsed.error)
  warnRefused(refused)

  const second = schema.safeParse(withoutKeys(raw, refused))
  return second.success ? second.data : schema.parse({})
}

/* ------------------------------------------------------------------ internals */

/** The top-level keys the schema had something to say about. */
function refusedKeys(error: z.ZodError): readonly string[] {
  const keys = new Set<string>()
  for (const issue of error.issues) {
    const [first] = issue.path
    if (typeof first === 'string') keys.add(first)
  }
  return [...keys]
}

/** The address with those keys taken out. Anything that is not an object is `{}`. */
function withoutKeys(raw: unknown, keys: readonly string[]): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null) return {}
  const dropped = new Set(keys)
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(([key]) => !dropped.has(key)),
  )
}

/**
 * Falling back is for somebody else's typing, not for ours.
 *
 * A refused value is almost always a guessed or stale URL, and rendering the page
 * is the right answer for that person. But it is the *same* answer when the portal
 * itself wrote the value — a seg that sends `?filter=Overdue`, a link built from a
 * renamed enum — and that one is a bug that would otherwise show up as a control
 * that silently does nothing. So development says so and production says nothing:
 * `import.meta.env.DEV` is a constant the build folds away, so the branch and the
 * sentence are both gone from the bundle.
 */
function warnRefused(refused: readonly string[]): void {
  if (refused.length === 0 || !import.meta.env.DEV) return
  console.warn(
    `[search] the address carries ${refused.join(', ')}, which this page's schema refuses; ` +
      'falling back to its defaults. If the portal wrote that value, the portal is wrong.',
  )
}
